import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoleContext } from "./RoleContext";
import { normalizeAppPath } from "../utils/normalizeAppPath";
import { getAppIdFromPath, APP_IDS, normalizeStoredAppId, ADMIN_ONLY_APP_IDS } from "../navigation/routeAppMap";
import { getDefaultPathForApp } from "../navigation/appMenus";
import { getAppLabel } from "../navigation/appMeta";
import { recordRecentApp } from "../navigation/appLauncherPreferences";
import { isCustomAppId, toClientAppId } from "../navigation/customAppIds";
import { fetchMyDefaultApp, fetchNavApps } from "../api/navAppsApi";
import { getDefaultRouteForRole } from "../utils/postLoginRoute";

const STORAGE_KEY = "ems_current_app";

export const AppContext = createContext(null);

function readStoredApp() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored = raw ? normalizeStoredAppId(raw) : null;
    if (stored && (Object.values(APP_IDS).includes(stored) || isCustomAppId(stored))) {
      if (raw && raw !== stored) {
        localStorage.setItem(STORAGE_KEY, stored);
      }
      return stored;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function pathMatchesCustomMenu(pathnameNorm, menuItems) {
  const p = pathnameNorm;
  if (!menuItems?.length) return false;
  return menuItems.some((it) => {
    const mp = (it.path || "").trim();
    if (!mp) return false;
    if (p === mp) return true;
    if (mp !== "/" && mp.endsWith("/") && p.startsWith(mp)) return true;
    if (mp !== "/" && !mp.endsWith("/") && (p === mp || p.startsWith(`${mp}/`))) return true;
    return false;
  });
}

export function AppProvider({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);

  const pathApp = useMemo(() => getAppIdFromPath(pathname), [pathname]);

  const [currentApp, setCurrentAppState] = useState(() => {
    if (typeof window !== "undefined") {
      const fromUrl = getAppIdFromPath(normalizeAppPath(window.location.pathname));
      if (fromUrl) return fromUrl;
    }
    return readStoredApp() || APP_IDS.LEADS;
  });

  const [appLauncherOpen, setAppLauncherOpen] = useState(false);
  const [defaultAppId, setDefaultAppId] = useState(null);

  /** API: all apps (system + custom) with menu_items on custom rows. */
  const [navApps, setNavApps] = useState([]);
  const [navAppsLoaded, setNavAppsLoaded] = useState(false);

  const menusByClientId = useMemo(() => {
    const map = {};
    for (const a of navApps) {
      if (!a.is_system && a.menu_items) {
        map[toClientAppId(a.id)] = a.menu_items;
      }
    }
    return map;
  }, [navApps]);

  const refreshNavApps = useCallback(async () => {
    try {
      const data = await fetchNavApps({ includeMenus: true, refresh: true });
      setNavApps(data.apps || []);
    } catch {
      setNavApps([]);
    } finally {
      setNavAppsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!role) return;
    refreshNavApps();
  }, [role, refreshNavApps]);

  useEffect(() => {
    if (!role) return;
    const isAdmin = (role || "").trim().toLowerCase() === "admin";
    if (!isAdmin && ADMIN_ONLY_APP_IDS.includes(currentApp)) {
      setCurrentAppState(APP_IDS.LEADS);
      try {
        localStorage.setItem(STORAGE_KEY, APP_IDS.LEADS);
      } catch {
        /* ignore */
      }
    }
  }, [role, currentApp]);

  useEffect(() => {
    let alive = true;
    if (!role) {
      setDefaultAppId(null);
      return undefined;
    }
    (async () => {
      try {
        const data = await fetchMyDefaultApp();
        if (!alive) return;
        setDefaultAppId(data?.app_id || null);
      } catch {
        if (alive) setDefaultAppId(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [role]);

  useEffect(() => {
    if (!navAppsLoaded || !isCustomAppId(currentApp)) return;
    const db = currentApp.replace(/^ca_/, "");
    const exists = navApps.some((a) => a.id === db && !a.is_system);
    if (!exists) {
      setCurrentAppState(APP_IDS.LEADS);
      try {
        localStorage.setItem(STORAGE_KEY, APP_IDS.LEADS);
      } catch {
        /* ignore */
      }
    }
  }, [navAppsLoaded, navApps, currentApp]);

  const switcherNavRef = useRef(false);

  useEffect(() => {
    const n = normalizeAppPath(pathname);
    const fromPath = getAppIdFromPath(n);

    if (switcherNavRef.current) {
      if (isCustomAppId(currentApp)) {
        const items = menusByClientId[currentApp];
        if (pathMatchesCustomMenu(n, items) || fromPath === currentApp) {
          switcherNavRef.current = false;
        }
      } else if (fromPath === currentApp) {
        switcherNavRef.current = false;
      }
      return;
    }

    if (!pathApp) return;

    if (isCustomAppId(currentApp)) {
      if (!navAppsLoaded) return;
      const items = menusByClientId[currentApp];
      if (pathMatchesCustomMenu(n, items)) return;
    }

    if (pathApp !== currentApp) {
      setCurrentAppState(pathApp);
      try {
        localStorage.setItem(STORAGE_KEY, pathApp);
      } catch {
        /* ignore */
      }
    }
  }, [pathname, pathApp, currentApp, menusByClientId, navAppsLoaded]);

  const navOptions = useMemo(
    () => ({ dynamicMenuItems: menusByClientId[currentApp] }),
    [menusByClientId, currentApp]
  );

  const setCurrentApp = useCallback(
    (nextApp, { skipNavigate = false } = {}) => {
      if (!nextApp || nextApp === currentApp) return;
      setCurrentAppState(nextApp);
      try {
        localStorage.setItem(STORAGE_KEY, nextApp);
        recordRecentApp(nextApp);
      } catch {
        /* ignore */
      }
      if (skipNavigate) return;

      const dyn = menusByClientId[nextApp];
      const dest = getDefaultPathForApp(nextApp, role, { dynamicMenuItems: dyn });
      const n = normalizeAppPath(pathname);
      let stillInTarget = false;
      if (isCustomAppId(nextApp)) {
        stillInTarget = pathMatchesCustomMenu(n, dyn);
      } else {
        stillInTarget = getAppIdFromPath(n) === nextApp;
      }
      if (!stillInTarget) {
        switcherNavRef.current = true;
        navigate(dest, { replace: false });
      }
    },
    [currentApp, navigate, pathname, role, menusByClientId]
  );

  const defaultAppliedRef = useRef(false);
  useEffect(() => {
    defaultAppliedRef.current = false;
  }, [role, defaultAppId]);

  useEffect(() => {
    if (!role || !defaultAppId || !navAppsLoaded || defaultAppliedRef.current) return;
    const landing = normalizeAppPath(getDefaultRouteForRole(role));
    const now = normalizeAppPath(pathname);
    if (now !== landing) return;
    defaultAppliedRef.current = true;
    setCurrentApp(defaultAppId);
  }, [role, defaultAppId, navAppsLoaded, pathname, setCurrentApp]);

  const resolveAppLabel = useCallback(
    (appId) => {
      if (!isCustomAppId(appId)) {
        return getAppLabel(appId);
      }
      const dbId = appId.replace(/^ca_/, "");
      const found = navApps.find((a) => a.id === dbId);
      return found?.name || appId;
    },
    [navApps]
  );

  /** Cmd/Ctrl+K and / open the in-sidebar app launcher (when not typing in a field). */
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      const editable = t?.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      if (editable) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAppLauncherOpen(true);
        return;
      }
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setAppLauncherOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo(
    () => ({
      currentApp,
      setCurrentApp,
      pathApp,
      appLauncherOpen,
      setAppLauncherOpen,
      apps: navApps,
      menuItemsByAppId: menusByClientId,
      navAppsLoaded,
      refreshNavApps,
      navOptions,
      resolveAppLabel,
    }),
    [
      currentApp,
      setCurrentApp,
      pathApp,
      appLauncherOpen,
      navApps,
      menusByClientId,
      navAppsLoaded,
      refreshNavApps,
      navOptions,
      resolveAppLabel,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppModule() {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppModule must be used within AppProvider");
  }
  return ctx;
}
